import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Instagram, Youtube, Facebook, Twitter, MessageCircle } from "lucide-react";
import SocialIcon from "@/components/ui/social-icon";
import defaultLogo from "@/assets/baelleaves-logo.png";
import { fetchAPI } from "@/lib/api";

interface Circuit {
  id: number;
  title: string;
}

export const Footer = () => {
  const [circuits, setCircuits] = useState<Circuit[]>([]);
  const [settings, setSettings] = useState<any>({});

  useEffect(() => {
    // Fetch Circuits
    fetchAPI("?type=circuits")
      .then((data: any) => setCircuits(data.slice(0, 6)));

    // Fetch Settings
    fetchAPI("?type=settings")
      .then((data: any) => {
        setSettings(data);
      });
  }, []);

  return (
    <footer className="bg-indigo text-primary-foreground">
      {/* Main Footer */}
      <div className="container mx-auto px-6 lg:px-8 pt-16 pb-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-6">
              <img loading="lazy" 
                src={settings.site_logo ? (settings.site_logo.startsWith("http") ? settings.site_logo : `https://baelleaves.com/${settings.site_logo}`) : defaultLogo} 
                alt="Bael Leaves" 
                className="h-16 w-16 object-cover rounded-full"
              />
            </div>
            <p className="text-primary-foreground/70 leading-relaxed mb-6">
              Reactivating the spiritual circuits. Guiding seekers on transformative journeys through India's sacred heritage.
            </p>
            <div className="flex gap-3">
              <a
                href={settings.social_facebook || "https://facebook.com/baelleaves"}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-primary-foreground/10 flex items-center justify-center hover:bg-primary-foreground/20 transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href={settings.social_instagram || "https://instagram.com/baelleaves"}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-primary-foreground/10 flex items-center justify-center hover:bg-primary-foreground/20 transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href={settings.social_youtube || "https://youtube.com/@baelleaves"}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-primary-foreground/10 flex items-center justify-center hover:bg-primary-foreground/20 transition-colors"
                aria-label="Youtube"
              >
                <Youtube className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Spiritual Circuits */}
          <div>
            <h3 className="font-serif text-lg mb-6">Spiritual Circuits</h3>
            <ul className="space-y-3">
              {circuits.map((circuit) => (
                <li key={circuit.id}>
                  <Link
                    to="/circuits"
                    className="text-primary-foreground/70 hover:text-primary-foreground transition-colors text-sm"
                  >
                    {circuit.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-serif text-lg mb-6">Explore</h4>
            <ul className="space-y-3">
              {[
                { label: "Gallery", href: "/gallery" },
                { label: "Stories", href: "/stories" },
                { label: "Baelleaves", href: "/baelleaves" },
              ].map((item) => (
                <li key={item.label}>
                  <Link
                    to={item.href}
                    className="text-primary-foreground/70 hover:text-primary-foreground transition-colors"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-serif text-lg mb-6">Connect With Us</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Mail className="w-5 h-5 mt-0.5 text-primary" />
                <a 
                  href={`mailto:${settings.contact_email || "info@baelleaves.com"}`}
                  className="text-primary-foreground/70 hover:text-primary-foreground transition-colors"
                >
                  {settings.contact_email || "info@baelleaves.com"}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <SocialIcon name="whatsapp" className="w-5 h-5 mt-0.5 text-primary" />
                <a 
                  href={`https://wa.me/${(settings.contact_whatsapp || "917042473737").replace(/\+/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-foreground/70 hover:text-primary-foreground transition-colors"
                >
                  WhatsApp Us
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Short Terms */}
        <div className="mt-8 pt-4 border-t border-primary-foreground/10">
          <p className="text-[12px] text-primary-foreground/50 text-center leading-relaxed">
            <span className="font-bold">Short Terms & Conditions: </span>
            By using this website, you agree to the Terms & Conditions of Baelleaves. All services are subject to availability, confirmation, and applicable policies. Prices, itineraries, and experiences may change due to operational or external factors. Content on this website is the intellectual property of Baelleaves. All disputes are subject to Indian law and jurisdiction.
          </p>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-primary-foreground/10">
        <div className="container mx-auto px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-primary-foreground/50">
            <p className="flex items-center gap-1">
              © 2024 Bael Leaves. All rights reserved. 
              <span className="hidden md:inline">|</span> 
              <span className="opacity-80">
                Created by <a href="https://adstoplay.com" target="_blank" rel="noopener noreferrer" className="hover:text-primary-foreground underlineDecoration-auto">Adstoplay</a>
              </span>
            </p>
            <div className="flex gap-6">
              <Link to="/privacy-policy" className="hover:text-primary-foreground transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms-of-service" className="hover:text-primary-foreground transition-colors">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};